<template>
  <div class="search-page">
    <div
      class="filter-container"
      :class="{expanded: filterOptionsExpanded}"
    >
      <div 
        class="filter-container-header"
        @click="filterOptionsExpanded = !filterOptionsExpanded"
      >
        Filter Results

        <span :class="filterOptionsExpanded ? 'up-arrow' : 'down-arrow'" />
      </div>
      <div
        class="filter-container-content"
        :class="{
          expanded: filterOptionsExpanded,
          collapsed: !filterOptionsExpanded,
        }"
      >
        <div class="input-group">
          <TextInput
            v-model="searchTerm"
            label="Search Term"
          />
        </div>
        <div class="button-wrapper">
          <button
            class="clear"
            @click="clearSearchForm"
          >
            Clear
          </button>
          <button
            class="search"
            @click="searchUsers"
          >
            Search
          </button>
        </div>
      </div>
    </div>
    <div class="results-container-header">
      <div class="results-container-header-text">
        Users
      </div>
    </div>
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading"
      class="overflow-wrapper"
    >
      <table>
        <thead>
          <tr>
            <th @click="sortBy('FirstName')">
              <div>
                First Name
                <span
                  v-show="sortField === 'FirstName'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('LastName')">
              <div>
                Last Name
                <span
                  v-show="sortField === 'LastName'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('Email')">
              <div>
                Email
                <span
                  v-show="sortField === 'Email'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('ReceivesEmailNotifications')">
              <div>
                Receives Email Notifications?
                <span
                  v-show="sortField === 'ReceivesEmailNotifications'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th class="no-sort">
              <div>
                Role
              </div>
            </th>
            <th class="no-sort" />
          </tr>
        </thead>
        <tbody v-if="users.length > 0">
          <tr
            v-for="user in users"
            :key="user.id"
          >
            <td>{{ user.firstName }}</td>
            <td>{{ user.lastName }}</td>
            <td>{{ user.email }}</td>
            <td>
              <Checkbox
                v-if="!isCustomer(user)"
                v-model="user.receivesEmailNotifications"
                class="input"
                label="Yes"
              />
            </td>
            <td>
              <Dropdown
                v-model="user.roleId"
                :disabled="!user.canEdit"
                label="Role"
                :options="roleDropdownOptions"
                @update:modelValue="onUserRoleChanged(user)"
              />
            </td>
            <td class="save">
              <button
                class="save-button"
                :disabled="!user.changed"
                @click="saveUser(user)"
              >
                Save
              </button>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td
              :colspan="6"
              class="message"
            >
              No users were found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Paginator
      v-if="!loading"
      class="paginator"
      :total-results="totalResults"
      :page-number="pageNumber"
      :page-size="pageSize"
      @page-change="onPageChanged"
      @page-size-change="onPageSizeChanged"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { UserService } from '@/services/user.service'
import Toaster from '@meforma/vue-toaster'
import { UserMessage } from '@/@types/messages/UserMessage'
import { RoleService } from '@/services/role.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { UserDetails } from '@/@types/responses/user/UserDetails'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Users',
  components: {
    Checkbox,
    Dropdown,
    LoadingPulse,
    Paginator,
    TextInput
  },
  setup() {
    const roleService = inject(PROVIDER_CONSTANTS.roleService) as RoleService
    const userService = inject(PROVIDER_CONSTANTS.userService) as UserService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      roleService,
      userService,
      toast
    }
  },
  data() {
    return {
      ascending: true,
      filterOptionsExpanded: true,
      loading: true,
      pageNumber: 1,
      pageSize: 10,
      roleDropdownOptions: [] as DropdownOptionResponse<string>[],
      searchTerm: '',
      sortField: 'FirstName',
      totalResults: 0,
      users: [] as UserDetails[]
    }
  },
  mounted() {
    window.addEventListener("popstate", this.parseQueryStringAndSearch)
    this.getRoles().then(() => {
      this.parseQueryStringAndSearch()
    })
  },
  unmounted() {
    window.removeEventListener('popstate', this.parseQueryStringAndSearch)
  },
  methods: {
    clearSearchForm(): void{
      this.searchTerm = ''
      this.search()
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getRoles(): Promise<void> {
      return this.roleService.getRoleDropdownOptions().then(dropdownOptionsResponse => {
        this.roleDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the roles. Try reloading the page.')
        }
      })
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    isCustomer(user: UserDetails): boolean {
      const role = this.roleDropdownOptions.find(r => r.id === user.roleId)
      return role?.name === 'Customer'
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchUsers()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onUserRoleChanged(user: UserDetails): void {
      if (this.isCustomer(user))
        user.receivesEmailNotifications = false
    },
    parseQueryStringAndSearch(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const searchTermFromUrl = this.getStringParameterByName("st")
      this.searchTerm = searchTermFromUrl !== undefined ? searchTermFromUrl : this.searchTerm

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      this.searchUsers()
    },
    saveUser(user: UserDetails): void {
      const userMessage = new UserMessage(user.receivesEmailNotifications, user.roleId)
      this.userService.updateUser(user.id, userMessage).then(() => {
        user.originalReceivesEmailNotifications = user.receivesEmailNotifications
        user.originalRoleId = user.roleId
        this.toast.success('User updated successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the user. Pleast try again.')
        }
      })
    },
    search(): void {
      this.pageNumber = 1
      this.searchUsers()
    },
    searchUsers(): void {
      this.loading = true
      this.updateUrl()
      this.userService.searchUsers(this.searchTerm, this.pageNumber, this.pageSize, this.sortField, this.ascending).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.users = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the users. Please try reloading the page.')
        }
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField

      this.searchUsers()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}`

      if (this.searchTerm !== '')
        newQueryString = newQueryString + `&st=${encodeURIComponent(this.searchTerm)}`
      
      this.$router.replace('/users' + newQueryString)
    }
  }
})
</script>

<style scoped lang="scss">
.search-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 15px;

  @include sac-search-page;
}
</style>
